import React, { useState, useEffect, useContext } from "react";
import {
  LoadingOverlay,
  Box,
  Container,
  NativeSelect,
  Alert,
  Select,
  Text,
  TextInput,
  Button,
  Group,
  Stack,
  Center,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { DataContext } from "../../../context/DataContext";
import {
  IconShieldCheck,
  IconShieldCancel,
  IconPhoneCheck,
} from "@tabler/icons-react";
import BasicDataCollectionForm from "../../../components/basic-data-collection-form";
import { validateDob } from "../../../utils/validateDob";
import useLocale from "../../../hooks/useLocale";

export default function IDMatchLegacy() {
  const { text, settings } = useContext(DataContext);
  const locale = useLocale("coppa");

  // fn + ln + ssn are always required for COPPA. fn/ln render first,
  // then journey-driven inputs, then ssn (rendered last so the
  // smallest / most sensitive field anchors the bottom of the form).
  // Same input-array shape every other form in the app uses
  // (idmatch/plus, agematch/process, phonematch).
  const nameFields = [
    {
      name: "fn",
      label: locale?.general?.firstName || text.general.firstName,
      initialValue: "",
      validation: (value) => {
        if (value.length < 2) {
          return locale?.general?.firstNameError || text.general.firstNameError;
        }
        if (/\d/.test(value)) {
          return locale?.validation?.firstNameNoNumbers || "First name cannot contain numbers";
        }
        return null;
      },
    },
    {
      name: "ln",
      label: locale?.general?.lastName || text.general.lastName,
      initialValue: "",
      validation: (value) => {
        if (value.length < 2) {
          return locale?.general?.lastNameError || text.general.lastNameError;
        }
        if (/\d/.test(value)) {
          return locale?.validation?.lastNameNoNumbers || "Last name cannot contain numbers";
        }
        return null;
      },
    },
  ];

  // SSN is always required for COPPA. By default the field is the
  // last 4 digits; if the journey's `inputs` array carries "ssn9",
  // we swap to a full 9-digit SSN field instead. The journey can't
  // ask for BOTH — "ssn9" overrides.
  const matchedRouteForSsn = settings?.journey?.route?.find(
    (route) => route.path === "idmatch/coppa"
  );
  const wantsFullSsn = Array.isArray(matchedRouteForSsn?.inputs)
    && matchedRouteForSsn.inputs.includes("ssn9");

  const ssnField = wantsFullSsn
    ? {
        name: "ssn",
        label:
          locale?.general?.fullSSN ||
          text?.general?.fullSSN ||
          "Social Security Number",
        initialValue: "",
        isMaskedInput: true,
        type: "tel",
        maxLength: 9,
        pattern: "[0-9]*",
        inputMode: "numeric",
        validation: (value) => {
          if (/[^0-9]/.test(value)) {
            return locale?.validation?.ssnOnlyNumbers || "SSN must contain only numbers";
          }
          if (value.length !== 9) {
            return (
              locale?.general?.fullSSNError ||
              text?.general?.fullSSNError ||
              "SSN must be 9 digits"
            );
          }
          return null;
        },
      }
    : {
        name: "ssn",
        label: locale?.general?.lastFourSSN || text.general.lastFourSSN,
        initialValue: "",
        isMaskedInput: true,
        type: "tel",
        maxLength: 4,
        pattern: "[0-9]*",
        inputMode: "numeric",
        validation: (value) => {
          if (/[^0-9]/.test(value)) {
            return locale?.validation?.ssnOnlyNumbers || "SSN must contain only numbers";
          }
          if (value.length !== 4) {
            return locale?.general?.lastFourSSNError || text.general.lastFourSSNError;
          }
          return null;
        },
      };

  const inputFieldMap = {
    dob: {
      name: "dob",
      label: locale?.general?.dateOfBirth || text.general.DateOfBirth,
      type: "date",
      validation: (value) =>
        validateDob(
          value,
          locale?.general?.dateOfBirthError ||
            text.general.DateOfBirthError ||
            "Invalid Date of Birth"
        ),
    },
    phone: {
      name: "phone",
      label: text?.general?.phone || "Phone Number",
      initialValue: "",
      validation: (value) => {
        if (!value) return text?.general?.phoneError || "Phone number is required";
        const digitsOnly = (value || "").replace(/\D/g, "");
        return digitsOnly.length !== 10
          ? text?.general?.phoneError || "Phone number must be 10 digits"
          : null;
      },
    },
    address: {
      name: "address",
      label: text?.general?.address || "Address",
      type: "address",
      showCityState: true,
      stacked: true,
      streetLabel: text?.general?.streetAddress || "Street Address",
      cityLabel: text?.general?.city || "City",
      stateLabel: text?.general?.state || "State",
      zipLabel: text?.general?.zip || "Zip",
      zipCodeLabel: text?.general?.zipCode || "Zip Code",
      errorMessage: text?.general?.addressError || "Address is required",
      validation: (value) => {
        if (value?.addr && value?.zip?.length === 5) return null;
        return text?.general?.addressError || "Address is required";
      },
    },
  };

  const matchedRoute = settings?.journey?.route?.find(
    (route) => route.path === "idmatch/coppa"
  );
  const routeInputs = matchedRoute?.inputs;

  // Build the input-driven part of the form. If the journey didn't
  // specify any inputs, default to DOB so existing configurations
  // keep working unchanged (SSN is now always required and lives in
  // `alwaysFields`).
  const inputFields =
    Array.isArray(routeInputs) && routeInputs.length > 0
      ? routeInputs.map((key) => inputFieldMap[key]).filter(Boolean)
      : [inputFieldMap.dob];

  const formConfig = {
    fields: [...nameFields, ...inputFields, ssnField],
  };

  // Local attempt counter follows the journey's per-route config so
  // the retry-banner math always matches what the backend will
  // actually allow. Falls back to the historical default of 2 when
  // the route node doesn't carry an `attempts` value.
  const routeAttempts =
    typeof matchedRoute?.attempts === "number" && matchedRoute.attempts > 0
      ? matchedRoute.attempts
      : 2;

  // Same route step setting as coppa-edge (off by default): only an opted-in
  // route skips the prefilled form.
  const autoSubmitPrefilled =
    matchedRoute?.settings?.auto_submit_prefilled === true;

  return (
    <BasicDataCollectionForm
      config={formConfig}
      title={locale?.coppa?.title || text.coppa.title}
      subtitle={locale?.coppa?.subTitle || text.coppa.subTitle}
      buttonText={locale?.coppa?.button || text.coppa.button}
      loadingText={locale?.form?.verifying}
      errorText={locale?.form?.somethingWentWrong}
      icon={<IconShieldCheck size={40} stroke={1.5} />}
      componentKey={"idmatch/coppa"}
      endpoint="/v3/stable/api/frontend/idmatchCoppa"
      reAttempts={routeAttempts}
      skipInitialAutoSubmit={!autoSubmitPrefilled}
    />
  );
}
